import React, { useState, useRef } from 'react';
import apiService from '../services/ApiService';

export interface ImageUploadProps {
  vehicleId: number;
  imageType: 'hero' | 'gallery' | 'modification';
  modificationId?: number;
  onImageUploaded: (imageData: any) => void;
  onError: (error: string) => void;
  maxFiles?: number;
  className?: string;
  onUploadComplete?: () => void;
  slotPosition?: number;
}

interface UploadProgress {
  fileName: string;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  error?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  vehicleId,
  imageType,
  modificationId,
  onImageUploaded,
  onError,
  maxFiles = 5,
  className = '',
  onUploadComplete,
  slotPosition
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
  const maxFileSize = 10 * 1024 * 1024; // 10MB

  const validateFile = (file: File): string | null => {
    if (!acceptedTypes.includes(file.type)) {
      return 'File type not supported. Please use JPEG, PNG, WebP, or GIF.';
    }

    if (file.size > maxFileSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
      return `File size too large (${fileSizeMB}MB). Maximum size is 10MB.`;
    }

    return null;
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const fileArray = Array.from(files);

    if (fileArray.length > maxFiles) {
      onError(`Maximum ${maxFiles} files allowed at once.`);
      return;
    }

    const validFiles: File[] = [];
    const errors: string[] = [];

    fileArray.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      onError(errors.join('\n'));
    }

    if (validFiles.length > 0) {
      uploadFiles(validFiles);
    }
  };

  const uploadFiles = async (files: File[]) => {
    setUploading(true);

    const progressArray: UploadProgress[] = files.map(file => ({
      fileName: file.name,
      progress: 0,
      status: 'uploading' as const
    }));

    setUploadProgress(progressArray);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        try {
          // Update progress for current file
          setUploadProgress(prev => prev.map((item, idx) =>
            idx === i ? { ...item, progress: 0 } : item
          ));

          const result = await apiService.uploadVehicleImage(vehicleId, file, imageType, modificationId, slotPosition);

          // Mark as success
          setUploadProgress(prev => prev.map((item, idx) =>
            idx === i ? { ...item, progress: 100, status: 'success' } : item
          ));

          onImageUploaded(result);

        } catch (error: any) {
          console.error(`Failed to upload ${file.name}:`, error);

          // Mark as error
          setUploadProgress(prev => prev.map((item, idx) =>
            idx === i ? {
              ...item,
              progress: 0,
              status: 'error',
              error: error.message || 'Upload failed'
            } : item
          ));

          // Also trigger the main error handler for user-facing error display
          let errorMessage = error.message || 'Upload failed';
          
          // Provide more helpful error messages for common server issues
          if (errorMessage.includes('File too large (server limit)')) {
            errorMessage = `${file.name}: File too large for server. Try reducing image size or quality (server limit may be lower than 10MB)`;
          } else if (errorMessage.includes('server limit')) {
            errorMessage = `${file.name}: ${errorMessage}. Try reducing image size or quality.`;
          } else {
            errorMessage = `${file.name}: ${errorMessage}`;
          }
          
          onError(errorMessage);
        }
      }
    } finally {
      setUploading(false);

      // Check if there are any errors in the current upload progress
      setTimeout(() => {
        setUploadProgress(currentProgress => {
          const hasErrors = currentProgress.some(item => item.status === 'error');
          if (hasErrors) {
            // Keep errors visible for 10 seconds
            setTimeout(() => {
              setUploadProgress([]);
            }, 7000); // Additional 7 seconds (total 10 seconds from start)
            return currentProgress;
          } else {
            // Clear successful uploads immediately after 3 seconds
            return [];
          }
        });
      }, 3000);
    }

    // Only call onUploadComplete if there are no errors
    if (onUploadComplete) {
      setUploadProgress(currentProgress => {
        const hasErrors = currentProgress.some(item => item.status === 'error');
        if (!hasErrors) {
          onUploadComplete();
        }
        return currentProgress;
      });
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    // Reset input to allow same file selection again
    e.target.value = '';
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
          ${dragOver
            ? 'border-blue-500 bg-blue-500/10'
            : 'border-white/[0.10] hover:border-white/[0.18] bg-[#141414]/50'
          }
          ${uploading ? 'pointer-events-none opacity-50' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={maxFiles > 1}
          accept={acceptedTypes.join(',')}
          onChange={handleFileChange}
          className="hidden"
          disabled={uploading}
        />

        <div className="space-y-4">
          <div className="flex justify-center">
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>

          <div>
            <p className="text-lg font-medium text-white mb-2">
              {uploading ? 'Uploading...' : 'Upload Images'}
            </p>
            <p className="text-sm text-gray-400 mb-2">
              Drag and drop {maxFiles > 1 ? `up to ${maxFiles} ` : ''}image{maxFiles > 1 ? 's' : ''} here, or click to select
            </p>
            <p className="text-xs text-gray-500">
              Supports: JPEG, PNG, WebP, GIF • Max size: 10MB each
            </p>
            <p className="text-xs text-orange-400 mt-1">
              Note: Server may have lower limits than 10MB
            </p>
          </div>

          {imageType === 'hero' && (
            <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-3">
              <p className="text-sm text-blue-300">
                <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                Hero images will be displayed in the main gallery carousel
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Upload Progress */}
      {uploadProgress.length > 0 && (
        <div className="space-y-2">
          {uploadProgress.map((item, index) => (
            <div key={index} className="bg-[#141414] rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white truncate flex-1 mr-2">
                  {item.fileName}
                </span>
                <span className="text-xs text-gray-400">
                  {item.status === 'uploading' && `${item.progress}%`}
                  {item.status === 'success' && (
                    <span className="text-green-400 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Success
                    </span>
                  )}
                  {item.status === 'error' && (
                    <span className="text-red-400 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L8.586 10 4.293 14.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                      Failed
                    </span>
                  )}
                </span>
              </div>

              {item.status === 'uploading' && (
                <div className="w-full bg-[#161616] rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${item.progress}%` }}
                  />
                </div>
              )}

              {item.status === 'error' && item.error && (
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-red-400 flex-1">{item.error}</p>
                  <button
                    onClick={() => {
                      setUploadProgress(prev => prev.filter((_, idx) => idx !== index));
                    }}
                    className="ml-2 text-red-400 hover:text-red-300 transition-colors"
                    title="Dismiss error"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export { ImageUpload }; 